import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getLossesDelays, convertToNotifications, LossDelayNotification } from '../utils/lossesApi';

// Use the interface from lossesApi for consistency
type NotificationRecord = LossDelayNotification;

export const NotificationsBell: React.FC = () => {
    const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch notifications from real API
    const fetchNotifications = async (): Promise<NotificationRecord[]> => {
        try {
            const response = await getLossesDelays();

            if (response.success && response.data) {
                // Convert API data to notification format
                const notifications = convertToNotifications(response.data);
                return notifications;
            } else {
                console.error('Failed to fetch losses and delays:', response.error);
                return [];
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            return [];
        }
    };

    // Polling de 30 segundos
    useEffect(() => {
        const loadNotifications = async () => {
            try {
                const data = await fetchNotifications();
                setNotifications(data);
            } catch (error) {
                console.error('Erro ao carregar notificações:', error);
            }
        };

        // Carregar imediatamente
        loadNotifications();

        // Configurar polling a cada 30 segundos
        const interval = setInterval(loadNotifications, 30000);

        return () => clearInterval(interval);
    }, []);

    // Fechar dropdown quando clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const markAsRead = (notificationId: string) => {
        setNotifications(prev =>
            prev.map(n =>
                n.id === notificationId ? { ...n, isRead: true } : n
            )
        );
    };

    const markAllAsRead = () => {
        setNotifications(prev =>
            prev.map(n => ({ ...n, isRead: true }))
        );
        toast.success('Todas as notificações marcadas como lidas');
    };

    const formatNotificationText = (notification: NotificationRecord): string => {
        const base = `[${notification.dia_mes}] ${notification.fabrica} – ${notification.filial} • ${notification.tipo}`;

        if (notification.tipo === 'Perda' && notification.novo_m3 !== undefined) {
            return `${base} • ${notification.novo_m3} m³`;
        }

        return base;
    };

    const formatTimestamp = (timestamp: string): string => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));

        if (diffMins < 1) return 'Agora';
        if (diffMins < 60) return `${diffMins}min atrás`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h atrás`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d atrás`;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors duration-200"
                title="Notificações"
            >
                <Bell className="w-5 h-5" />

                {/* Badge */}
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center font-medium">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900">
                            Notificações
                        </h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Marcar todas como lidas
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-64 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="px-4 py-6 text-center text-gray-500">
                                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">Nenhuma notificação</p>
                            </div>
                        ) : (
                            <div className="py-2">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${!notification.isRead ? 'bg-blue-50' : ''
                                            }`}
                                        onClick={() => markAsRead(notification.id)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm ${!notification.isRead ? 'font-medium text-gray-900' : 'text-gray-700'
                                                    }`}>
                                                    {formatNotificationText(notification)}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formatTimestamp(notification.timestamp)}
                                                </p>
                                            </div>
                                            {!notification.isRead && (
                                                <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 flex-shrink-0 mt-1"></div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
                            <p className="text-xs text-gray-500 text-center">
                                {unreadCount} não lida{unreadCount !== 1 ? 's' : ''} de {notifications.length} notificação{notifications.length !== 1 ? 'es' : ''}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}; 